/**
 * BlueprintMinerals — "¿qué asteroides minar para fabricar X?"
 *
 *  - Input: nombre EXACTO del product (ej: "Hobgoblin I", no "Hob") o
 *    type_id numérico (ej: 2454). Fuzzwork solo hace match exacto —
 *    para buscar nombres, ver https://www.fuzzwork.co.uk/.
 *  - Output:
 *      1) Por cada material: top 3 ores rankeadas por least-ore-to-mine.
 *      2) Mining plan: ores que producen VARIOS materiales del BP a la vez
 *         (batch yield) — esto es lo que el industrialist quiere ver para
 *         minar de forma eficiente (ej: Scordite cubre Trit + Pyr juntos).
 *  - Refining efficiency default 0.7 (~70%, maxed refining 5 + station).
 *    Cambiable en el form.
 *  - Costo total Jita buy de los minerales, para comparar con minar.
 *
 * Caveats:
 *  - Solo ores high-sec/null-sec comunes. Moon ores (R64) y variantes raras
 *    no aparecen como recomendación (la tabla oreRefining tiene ~40 ores).
 *  - Yields son base (sin refining skill) — multiplicados por tu refining
 *    efficiency en el backend.
 *  - Si el item no tiene BP (ej: Tritanium, Morphite — son raw minerals),
 *    devuelve 200 con materials=[] + mining_plan=[] + nota explicativa.
 *  - No chequea si el user tiene el BP — es lookup genérico.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, type BlueprintMineralsResponse } from '../api'
import { LoadingSkeleton } from '../components/LoadingSkeleton'

const QUICK_BPS = [
  { type_id: 2454, name: 'Hobgoblin I' },
  { type_id: 12005, name: 'Acolyte I' },
  { type_id: 11541, name: 'Small Trimark Armor Pump I' },
  { type_id: 587, name: 'Rifter' },
]

export function BlueprintMinerals() {
  const [input, setInput] = useState('')
  const [submitted, setSubmitted] = useState<
    { typeId: number } | { name: string } | null
  >(null)
  const [runs, setRuns] = useState(1)
  const [refiningEfficiency, setRefiningEfficiency] = useState(0.7)

  const query = useQuery({
    queryKey: ['blueprint-minerals', submitted, runs, refiningEfficiency],
    queryFn: () => {
      if (submitted === null) throw new Error('no submitted')
      if ('typeId' in submitted) {
        return api.blueprintMinerals(submitted.typeId, runs, refiningEfficiency)
      }
      return api.blueprintMineralsByName(submitted.name, runs, refiningEfficiency)
    },
    enabled: submitted !== null,
    retry: false,
  })

  function submit(e?: React.FormEvent) {
    e?.preventDefault()
    const trimmed = input.trim()
    if (!trimmed) return
    // Si es puramente numérico, se envía como type_id. Si no, como name.
    if (/^\d+$/.test(trimmed)) {
      const parsed = parseInt(trimmed, 10)
      if (!Number.isFinite(parsed) || parsed <= 0) return
      setSubmitted({ typeId: parsed })
    } else {
      setSubmitted({ name: trimmed })
    }
  }

  function quickPick(typeId: number, name: string) {
    setInput(name)
    setSubmitted({ typeId })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">BP → Ores</h1>
        <p className="text-eve-muted text-sm">
          Introduce el nombre exacto del item (ej:{' '}
          <span className="font-mono">Hobgoblin I</span>) o su type_id numérico
          (ej: <span className="font-mono">2454</span>); el sistema mostrará los
          minerales que requiere, qué asteroides minar, y cuántas unidades. Para
          localizar el nombre, consulta{' '}
          <a
            href="https://www.fuzzwork.co.uk/"
            target="_blank"
            rel="noreferrer"
            className="text-eve-accent hover:underline"
          >
            fuzzwork.co.uk
          </a>
          .
        </p>
      </div>

      {/* Search form */}
      <form
        onSubmit={submit}
        className="bg-eve-panel border border-eve-border rounded-md p-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5">
            <label className="block text-xs uppercase tracking-wider text-eve-muted mb-1">
              Nombre exacto o type_id
            </label>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="ej: Hobgoblin I · 2454"
              className="w-full bg-eve-bg border border-eve-border rounded px-2 py-1.5 font-mono text-sm focus:outline-none focus:border-eve-accent"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-eve-muted mb-1">
              Runs
            </label>
            <input
              type="number"
              min={1}
              value={runs}
              onChange={(e) =>
                setRuns(Math.max(1, parseInt(e.target.value, 10) || 1))
              }
              className="w-full bg-eve-bg border border-eve-border rounded px-2 py-1.5 font-mono text-sm focus:outline-none focus:border-eve-accent"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs uppercase tracking-wider text-eve-muted mb-1">
              Refining efficiency ({Math.round(refiningEfficiency * 100)}%)
            </label>
            <input
              type="range"
              min={0.3}
              max={0.9}
              step={0.05}
              value={refiningEfficiency}
              onChange={(e) =>
                setRefiningEfficiency(parseFloat(e.target.value))
              }
              className="w-full accent-eve-accent"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              className="w-full bg-eve-accent text-eve-bg font-semibold rounded px-3 py-1.5 text-sm hover:bg-eve-accent-hover transition-colors"
            >
              Look up
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-eve-muted">
            Quick picks:
          </span>
          {QUICK_BPS.map((bp) => (
            <button
              key={bp.type_id}
              type="button"
              onClick={() => quickPick(bp.type_id, bp.name)}
              className="text-[11px] px-2 py-0.5 rounded border border-eve-border bg-eve-bg hover:bg-eve-panel hover:border-eve-accent/40 text-eve-muted hover:text-eve-text transition-colors"
              title={bp.name}
            >
              {bp.name}
            </button>
          ))}
        </div>
      </form>

      {/* Error state */}
      {query.isError && (
        <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-4 text-eve-danger text-sm">
          {(query.error as Error).message}
        </div>
      )}

      {/* Empty state */}
      {submitted === null && (
        <div className="bg-eve-panel border border-eve-border rounded-md p-8 text-center text-eve-muted text-sm">
          Introduce un nombre o type_id y pulsa "Look up" para comenzar.
        </div>
      )}

      {/* Loading */}
      {query.isLoading && <LoadingSkeleton variant="rows" rows={6} />}

      {/* Results */}
      {query.data && <Results data={query.data} />}
    </div>
  )
}

function Results({ data }: { data: BlueprintMineralsResponse }) {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-eve-panel border border-eve-border rounded-md p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-eve-muted">
              Blueprint
            </div>
            <div className="text-xl font-semibold text-eve-text mt-0.5">
              {data.blueprint.name}
            </div>
            <div className="text-[11px] text-eve-muted font-mono mt-0.5">
              type_id {data.blueprint.type_id} · {data.runs} run
              {data.runs === 1 ? '' : 's'} ·{' '}
              {Math.round(data.refining_efficiency * 100)}% refining
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-eve-muted">
              Total Jita buy
            </div>
            <div className="font-mono text-lg font-semibold text-eve-text mt-0.5">
              {formatISK(data.total_jita_buy)} ISK
            </div>
            <div className="text-[10px] text-eve-muted mt-0.5 italic">
              alternativa: comprar vs minar
            </div>
          </div>
        </div>
      </div>

      {/* No-BP note: type válido pero sin blueprint (ej: minerales raw) */}
      {data.note && data.materials.length === 0 && (
        <div className="bg-eve-warn/10 border border-eve-warn/30 rounded-md p-4 text-eve-warn text-sm">
          {data.note}
        </div>
      )}

      {/* Mining plan — ores que cubren varios materiales a la vez */}
      {data.mining_plan.length > 0 && (
        <div className="bg-eve-panel border border-eve-border rounded-md p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-eve-text">
              Mining plan
            </h2>
            <span className="text-[10px] text-eve-muted italic">
              ores que cubren varios materiales a la vez (batch yield)
            </span>
          </div>
          <div className="space-y-3">
            {data.mining_plan.map((entry, i) => (
              <MiningPlanRow
                key={entry.type_id}
                entry={entry}
                star={i === 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-material recommendations */}
      {data.materials.length > 0 && (
        <div className="bg-eve-panel border border-eve-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-eve-bg/60">
              <tr className="text-[10px] uppercase tracking-wider text-eve-muted">
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Jita buy</th>
                <th className="text-right px-3 py-2">Cost</th>
                <th className="text-left px-3 py-2">Ore (top 3)</th>
              </tr>
            </thead>
            <tbody>
              {data.materials.map((m) => (
                <tr
                  key={m.type_id}
                  className="border-t border-eve-border/60 hover:bg-eve-bg/40"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-eve-text">{m.name}</div>
                    <div className="text-[10px] text-eve-muted font-mono">
                      #{m.type_id}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-eve-text">
                    {m.quantity_total.toLocaleString('en-US')}
                    <div className="text-[10px] text-eve-muted">
                      {m.quantity_per_run}/run
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-eve-text">
                    {m.jita_buy !== null ? formatISK(m.jita_buy) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-eve-text">
                    {m.total_cost_isk > 0 ? formatISK(m.total_cost_isk) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {m.ores.length === 0 ? (
                      <span className="text-eve-muted text-xs italic">
                        sin recomendación
                      </span>
                    ) : (
                      <div className="space-y-0.5">
                        {m.ores.map((o, idx) => (
                          <div
                            key={o.type_id}
                            className="flex items-baseline gap-2 text-xs"
                          >
                            <span
                              className={
                                idx === 0
                                  ? 'font-semibold text-eve-text'
                                  : 'text-eve-muted'
                              }
                            >
                              {idx === 0 ? '★ ' : '  '}
                              {o.name}
                            </span>
                            <span
                              className={
                                idx === 0
                                  ? 'font-mono text-eve-accent'
                                  : 'font-mono text-eve-muted'
                              }
                            >
                              {o.qty_to_mine.toLocaleString('en-US')}
                            </span>
                            <span className="text-[10px] text-eve-muted">
                              @ {o.yield_per_100}/100
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.materials.length > 0 && (
        <p className="text-xs text-eve-muted italic">
          Los yields son base (sin refining skill), multiplicados por tu
          efficiency ({Math.round(data.refining_efficiency * 100)}%). La ore
          con ★ es la que menos unidades requiere para conseguir el mineral
          indicado; el "Mining plan" muestra la opción que más materiales del
          BP cubre simultáneamente.
        </p>
      )}
    </div>
  )
}

function MiningPlanRow({
  entry,
  star,
}: {
  entry: BlueprintMineralsResponse['mining_plan'][number]
  star: boolean
}) {
  return (
    <div
      className={`rounded border ${
        star
          ? 'border-eve-accent/40 bg-eve-accent/5'
          : 'border-eve-border/60 bg-eve-bg/30'
      } p-3`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1.5">
        <div
          className={`font-medium ${star ? 'text-eve-text' : 'text-eve-text/90'}`}
        >
          {star && <span className="text-eve-accent mr-1">★</span>}
          {entry.name}
          <span className="text-[10px] text-eve-muted font-mono ml-2">
            #{entry.type_id}
          </span>
          <span className="text-[10px] text-eve-muted ml-2 italic">
            cubre {entry.yields.length}{' '}
            {entry.yields.length === 1 ? 'material' : 'materiales'}
          </span>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-eve-muted">
            Minar
          </div>
          <div className="font-mono text-sm font-semibold text-eve-text">
            {entry.qty_to_mine.toLocaleString('en-US')} und
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        {entry.yields.map((y) => (
          <div
            key={y.material_id}
            className="flex items-baseline justify-between text-xs gap-2"
          >
            <div className="flex items-baseline gap-1.5">
              <span className="text-eve-text">{y.material_name}</span>
              <span className="text-eve-muted font-mono">
                +{y.units_gained.toLocaleString('en-US')}
              </span>
              <span className="text-[10px] text-eve-muted">
                (necesita {y.units_needed.toLocaleString('en-US')})
              </span>
            </div>
            <span
              className={
                y.pct_covered >= 100
                  ? 'text-eve-success font-mono font-semibold'
                  : y.pct_covered >= 50
                    ? 'text-eve-accent font-mono'
                    : 'text-eve-warn font-mono'
              }
            >
              {Math.round(y.pct_covered)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
